import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getPosterAdminStatistics, type PosterAdminFilter } from "../api/posters";
import { useCampaigns } from "../api/campaigns";
import { PosterAdminFilters, PosterAdminTabs, defaultPosterFilter } from "./components/PosterAdminNav";
import "./poster-admin.css";

const fmt = (n: number) => new Intl.NumberFormat("sv-SE").format(n);

export const weekKey = (isoDate: string): string => {
    // ISO-vecka: torsdagen i samma vecka avgör vilket år och vilken vecka datumet hör till.
    const date = new Date(`${isoDate}T00:00:00Z`);
    const day = (date.getUTCDay() + 6) % 7;
    date.setUTCDate(date.getUTCDate() - day + 3);
    const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
    const firstDay = (firstThursday.getUTCDay() + 6) % 7;
    firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDay + 3);
    const week = 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
    return `${date.getUTCFullYear()}-v${String(week).padStart(2, "0")}`;
};

export const PosterStatisticsPage = (): JSX.Element => {
    const [filter, setFilter] = useState<PosterAdminFilter>(defaultPosterFilter);
    const [grouping, setGrouping] = useState<"day" | "week">("day");

    const { data: campaigns = [] } = useCampaigns();
    const { data, isLoading } = useQuery({
        queryKey: ["posters", "admin", "statistics", filter],
        queryFn: () => getPosterAdminStatistics(filter),
        staleTime: 60_000
    });

    const overview = data?.overview;
    const daily = data?.daily ?? [];

    const chartData = useMemo(() => {
        if (grouping === "day") {
            return daily.map((row) => ({
                label: row.date,
                placed: row.placedQuantity,
                takenDown: row.takenDownQuantity,
                vandalized: row.vandalizedQuantity
            }));
        }

        const byWeek = new Map<string, { placed: number; takenDown: number; vandalized: number }>();
        for (const row of daily) {
            const key = weekKey(row.date);
            const bucket = byWeek.get(key) ?? { placed: 0, takenDown: 0, vandalized: 0 };
            bucket.placed += row.placedQuantity;
            bucket.takenDown += row.takenDownQuantity;
            bucket.vandalized += row.vandalizedQuantity;
            byWeek.set(key, bucket);
        }
        return [...byWeek.entries()].map(([label, value]) => ({ label, ...value }));
    }, [daily, grouping]);

    const averagePerActiveDay = daily.length > 0
        ? Math.round(daily.reduce((sum, row) => sum + row.placedQuantity, 0) / daily.length)
        : 0;

    const bestDay = daily.reduce<{ date: string; placedQuantity: number } | null>(
        (best, row) => (best === null || row.placedQuantity > best.placedQuantity ? row : best),
        null
    );

    const exportCsv = () => {
        const headers = ["Datum", "Uppsatta", "Registreringar", "Nedtagna", "Vandaliserade"];
        const rows = daily.map((row) => [
            row.date,
            row.placedQuantity,
            row.placedEntries,
            row.takenDownQuantity,
            row.vandalizedQuantity
        ]);
        const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
        const link = document.createElement("a");
        link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
        link.download = `affischer-per-dag-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
    };

    return (
        <section className="poster-admin" aria-label="Affischstatistik">
            <div className="poster-admin__header">
                <h2>Affischering</h2>
                <button
                    type="button"
                    className="poster-admin__button"
                    onClick={exportCsv}
                    disabled={daily.length === 0}
                >
                    Exportera CSV
                </button>
            </div>

            <PosterAdminTabs />

            <PosterAdminFilters
                filter={filter}
                onChange={setFilter}
                campaigns={campaigns}
                extra={
                    <div className="filter-group">
                        <label htmlFor="poster-grouping">Gruppering:</label>
                        <select
                            id="poster-grouping"
                            className="filter-select"
                            value={grouping}
                            onChange={(e) => setGrouping(e.target.value as "day" | "week")}
                        >
                            <option value="day">Per dag</option>
                            <option value="week">Per vecka</option>
                        </select>
                    </div>
                }
            />

            {isLoading && <p className="poster-admin__empty">Hämtar statistik…</p>}

            {overview && (
                <div className="poster-admin__summary">
                    <div className="summary-card">
                        <p className="summary-card__label">Uppsatta totalt</p>
                        <p className="summary-card__value">{fmt(overview.placedQuantity)}</p>
                    </div>
                    <div className="summary-card">
                        <p className="summary-card__label">Uppe nu</p>
                        <p className="summary-card__value">{fmt(overview.activeQuantity)}</p>
                    </div>
                    <div className="summary-card">
                        <p className="summary-card__label">Nedtagna</p>
                        <p className="summary-card__value">{fmt(overview.takenDownQuantity)}</p>
                    </div>
                    <div className="summary-card">
                        <p className="summary-card__label">Vandaliserade</p>
                        <p className="summary-card__value">{fmt(overview.vandalizedQuantity)}</p>
                    </div>
                    <div className="summary-card">
                        <p className="summary-card__label">Snitt per aktiv dag</p>
                        <p className="summary-card__value">{fmt(averagePerActiveDay)}</p>
                    </div>
                    <div className="summary-card">
                        <p className="summary-card__label">Affischerare</p>
                        <p className="summary-card__value">{fmt(overview.distinctPlacers)}</p>
                    </div>
                </div>
            )}

            {bestDay && (
                <p className="poster-admin__hint">
                    Bästa dagen: <strong>{bestDay.date}</strong> med {fmt(bestDay.placedQuantity)} affischer.
                    {overview?.firstDate && overview.lastDate && (
                        <> Perioden i urvalet: {overview.firstDate} – {overview.lastDate}.</>
                    )}
                </p>
            )}

            {chartData.length > 0 && (
                <div className="poster-admin__chart">
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="placed" name="Uppsatta" fill="#114838" />
                            <Bar dataKey="takenDown" name="Nedtagna" fill="#94a3b8" />
                            <Bar dataKey="vandalized" name="Vandaliserade" fill="#D92626" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            <h3 className="poster-admin__section-title">Per dag</h3>
            <div className="poster-admin__table-container">
                <table className="poster-admin__table">
                    <thead>
                        <tr>
                            <th>Datum</th>
                            <th>Uppsatta</th>
                            <th>Registreringar</th>
                            <th>Nedtagna</th>
                            <th>Vandaliserade</th>
                        </tr>
                    </thead>
                    <tbody>
                        {daily.length === 0 && !isLoading && (
                            <tr>
                                <td colSpan={5} className="poster-admin__empty">
                                    Inga affischer registrerade i urvalet.
                                </td>
                            </tr>
                        )}
                        {[...daily].reverse().map((row) => (
                            <tr key={row.date}>
                                <td>{row.date}</td>
                                <td>{fmt(row.placedQuantity)}</td>
                                <td>{fmt(row.placedEntries)}</td>
                                <td>{fmt(row.takenDownQuantity)}</td>
                                <td>{fmt(row.vandalizedQuantity)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <h3 className="poster-admin__section-title">Per person</h3>
            <div className="poster-admin__table-container">
                <table className="poster-admin__table">
                    <thead>
                        <tr>
                            <th>Person</th>
                            <th>Uppsatta</th>
                            <th>Uppe nu</th>
                            <th>Nedtagna</th>
                            <th>Vandaliserade</th>
                            <th>Senast</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data?.byUser ?? []).length === 0 && !isLoading && (
                            <tr>
                                <td colSpan={6} className="poster-admin__empty">Inga affischerare i urvalet.</td>
                            </tr>
                        )}
                        {(data?.byUser ?? []).map((row) => (
                            <tr key={row.userId}>
                                <td>{row.userName}</td>
                                <td>{fmt(row.placedQuantity)}</td>
                                <td>{fmt(row.activeQuantity)}</td>
                                <td>{fmt(row.takenDownQuantity)}</td>
                                <td>{fmt(row.vandalizedQuantity)}</td>
                                <td>
                                    {row.lastPlacedAt
                                        ? new Date(row.lastPlacedAt).toLocaleString("sv-SE", {
                                              dateStyle: "short",
                                              timeStyle: "short"
                                          })
                                        : "–"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {(data?.byCampaign ?? []).length > 1 && (
                <>
                    <h3 className="poster-admin__section-title">Per kampanj</h3>
                    <div className="poster-admin__table-container">
                        <table className="poster-admin__table">
                            <thead>
                                <tr>
                                    <th>Kampanj</th>
                                    <th>Uppsatta</th>
                                    <th>Uppe nu</th>
                                    <th>Nedtagna</th>
                                    <th>Vandaliserade</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data?.byCampaign ?? []).map((row) => (
                                    <tr key={row.campaignId}>
                                        <td>{row.campaignName}</td>
                                        <td>{fmt(row.placedQuantity)}</td>
                                        <td>{fmt(row.activeQuantity)}</td>
                                        <td>{fmt(row.takenDownQuantity)}</td>
                                        <td>{fmt(row.vandalizedQuantity)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </section>
    );
};
